import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { GraduationCap, Paperclip, Camera, FileText, X } from "lucide-react";
import Header from "@/components/Header";
import { Card, TextInput, NumberInput, Select, Field } from "@/components/ui-kit";
import { StickySubmit } from "./TemperatureForm";
import { createRecord, updateRecord, uploadFile } from "@/lib/api";
import { resizeImage } from "@/lib/image";
import { getUser } from "@/lib/auth";

export default function TrainingForm() {
  const navigate = useNavigate();
  const editRecord = useLocation().state?.editRecord;
  const today  = new Date().toISOString().slice(0, 10);
  const signer = getUser()?.username || "";
  const [form, setForm] = useState(editRecord
    ? { date: editRecord.date, person_name: editRecord.person_name, position: editRecord.position, activity_name: editRecord.activity_name, training_type: editRecord.training_type, hours: String(editRecord.hours ?? ""), training_company: editRecord.training_company, cert_code: editRecord.cert_code || "" }
    : { date: today, person_name: "", position: "", activity_name: "", training_type: "I", hours: "", training_company: "", cert_code: "" });
  const [documents, setDocuments] = useState(editRecord?.documents || []);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleDocs = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const f = await resizeImage(file);
        const { url } = await uploadFile(f);
        setDocuments((d) => [...d, url]);
      }
    } catch { toast.error("No s'ha pogut pujar el document"); }
    finally { setUploading(false); }
  };
  const removeDoc = (url) => setDocuments((d) => d.filter((u) => u !== url));

  const submit = async () => {
    if (!form.person_name || !form.position || !form.activity_name || !form.hours || !form.training_company)
      return toast.error("Omple els camps obligatoris");
    setSubmitting(true);
    try {
      const payload = { ...form, hours: parseFloat(form.hours), documents, signer };
      if (editRecord) await updateRecord("training", editRecord.id, payload);
      else await createRecord("training", payload);
      toast.success(editRecord ? "Formació modificada" : "Formacio registrada");
      navigate("/history?type=training");
    } catch { toast.error("Error desant el registre"); }
    finally   { setSubmitting(false); }
  };

  return (
    <>
      <Header title="Formacio" />
      <div className="mx-auto max-w-2xl px-4 pb-32 pt-6 sm:px-6 sm:pt-10">
        <div className="animate-fade-up flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-ink text-white"><GraduationCap size={22} /></div>
          <div><div className="label-eyebrow">Quan calgui</div><h2 className="font-display text-2xl font-bold text-ink sm:text-3xl">Activitat formativa</h2></div>
        </div>
        <div className="mt-6 space-y-4">
          <Card><div className="space-y-4">
            <Field label="Data"><input data-testid="training-date" type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className="h-14 w-full rounded-xl border border-line bg-white px-4 text-base text-ink outline-none focus:border-terracotta focus:ring-2 focus:ring-terracotta/20" /></Field>
            <Field label="Nom i cognoms"><TextInput testid="training-person" placeholder="Qui ha fet la formacio" value={form.person_name} onChange={(e) => set("person_name", e.target.value)} /></Field>
            <Field label="Lloc de treball"><TextInput testid="training-position" placeholder="Ex: Carnisser/a" value={form.position} onChange={(e) => set("position", e.target.value)} /></Field>
            <Field label="Nom de l'activitat formativa"><TextInput testid="training-activity" placeholder="Ex: Manipulacio d'aliments" value={form.activity_name} onChange={(e) => set("activity_name", e.target.value)} /></Field>
            <Field label="Tipus"><Select testid="training-type" value={form.training_type} onChange={(e) => set("training_type", e.target.value)}><option value="I">Inicial</option><option value="C">Continua</option></Select></Field>
            <Field label="Hores"><NumberInput testid="training-hours" placeholder="0" value={form.hours} onChange={(e) => set("hours", e.target.value)} /></Field>
            <Field label="Empresa formadora"><TextInput testid="training-company" placeholder="Nom de l'empresa" value={form.training_company} onChange={(e) => set("training_company", e.target.value)} /></Field>
            <Field label="Codi certificat (opcional)"><TextInput testid="training-cert" placeholder="Num. de certificat" value={form.cert_code} onChange={(e) => set("cert_code", e.target.value)} /></Field>
            <div>
              <div className="label-eyebrow mb-2">Certificats / documents (PDF o foto)</div>
              <div className="space-y-2">
                {documents.map((url) => (
                  <div key={url} className="flex items-center gap-2 rounded-xl border border-line bg-white px-3 py-2">
                    <FileText size={16} className="shrink-0 text-ink-soft" />
                    <a href={url} target="_blank" rel="noopener noreferrer" className="flex-1 truncate text-sm text-terracotta underline">{url.split("/").pop()}</a>
                    <button type="button" onClick={() => removeDoc(url)} className="shrink-0 text-ink-soft hover:text-ember" aria-label="Treure"><X size={16} /></button>
                  </div>
                ))}
                <div className="flex gap-2">
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                    <Camera size={16} /> {uploading ? "Pujant…" : "Càmera"}
                    <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleDocs} disabled={uploading} />
                  </label>
                  <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-white px-3 py-3 text-sm font-semibold text-ink-soft transition-colors hover:border-terracotta hover:text-terracotta">
                    <Paperclip size={16} /> Document
                    <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleDocs} disabled={uploading} />
                  </label>
                </div>
              </div>
            </div>
          </div></Card>
        </div>
      </div>
      <StickySubmit onClick={submit} disabled={submitting || uploading} label={submitting ? "Desant..." : editRecord ? "Desar canvis" : "Desar formacio"} />
    </>
  );
}
